/**
 * threadId 丢失取证探针(临时排查用)。
 *
 * 背景:线上偶发「/thread 硬刷新后 URL 只剩 agentId、threadId 被摘」,
 * 手动开 Network 抓不到(偶发 + 刷新后 document 请求已过)。
 *
 * 本探针把证据落到 localStorage(跨刷新保留),事后在 Console 敲
 * `__threadIdAudit()` 即可打印全部记录,直接定位真凶层次:
 * - type=boot 且 hadThreadId=false，而上一条 lastSeen 有 threadId
 *   → 刷新那一刻地址栏就没 threadId(服务端/边缘/SW 重定向摘的,或刷新前已脱节)
 * - type=strip 带 stack → 是 JS 主动摘的,stack 直接指向那行代码
 *
 * 排查完删除本文件与 app.tsx 顶部的 import 即可,无副作用残留。
 */

const STORAGE_KEY = '__threadId_audit_log__';
const LAST_SEEN_KEY = '__threadId_audit_lastseen__';
const MAX_ENTRIES = 60;

type AuditEntry = {
  t: string; // 北京时间可读串
  type: 'boot' | 'strip' | 'set';
  path: string;
  before?: string; // 变更前 threadId
  after?: string; // 变更后 threadId
  prevUrl?: string;
  nextUrl?: string;
  stack?: string;
};

function nowStr(): string {
  // 直接用本地时区可读串,排查用不纠结时区换算
  return new Date().toLocaleString('sv');
}

function getThreadId(search: string): string | undefined {
  try {
    return new URLSearchParams(search).get('threadId') || undefined;
  } catch {
    return undefined;
  }
}

function readLog(): AuditEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuditEntry[]) : [];
  } catch {
    return [];
  }
}

function pushLog(entry: AuditEntry): void {
  try {
    const log = readLog();
    log.push(entry);
    if (log.length > MAX_ENTRIES) log.splice(0, log.length - MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch {
    /* ignore */
  }
}

function onThreadPage(pathname: string): boolean {
  return pathname.startsWith('/thread');
}

let installed = false;

export function installThreadIdAudit(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1) 启动快照:记录页面加载那一刻地址栏有没有 threadId
  try {
    const { pathname, search } = window.location;
    if (onThreadPage(pathname)) {
      const current = getThreadId(search);
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || undefined;
      pushLog({
        t: nowStr(),
        type: 'boot',
        path: pathname + search,
        after: current,
        // 上一次会话里最后见到的 threadId,用来判断这次 boot 是不是"丢了"
        before: lastSeen,
      });
    }
  } catch {
    /* ignore */
  }

  // 2) 给 history 打补丁:任何 JS 改地址栏都经过这里
  const patch = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method].bind(window.history);
    window.history[method] = function patched(
      this: History,
      data: any,
      unused: string,
      url?: string | URL | null,
    ) {
      try {
        const prevUrl = window.location.pathname + window.location.search;
        const before = getThreadId(window.location.search);
        const result = original(data, unused, url as any);
        const nextSearch = window.location.search;
        const after = getThreadId(nextSearch);
        const pathname = window.location.pathname;

        if (onThreadPage(pathname)) {
          // 记录 threadId 存在时的最新值,供下次 boot 对比
          if (after) localStorage.setItem(LAST_SEEN_KEY, after);

          // 只在 thread 页、且 threadId 从"有"变"没"时记 strip(带堆栈,定位元凶)
          if (before && !after) {
            pushLog({
              t: nowStr(),
              type: 'strip',
              path: pathname,
              before,
              after,
              prevUrl,
              nextUrl: pathname + nextSearch,
              stack: new Error('threadId stripped').stack,
            });
          } else if (before !== after) {
            pushLog({ t: nowStr(), type: 'set', path: pathname, before, after });
          }
        }
        return result;
      } catch {
        return original(data, unused, url as any);
      }
    } as History[typeof method];
  };

  patch('pushState');
  patch('replaceState');

  // 3) 暴露读取入口:事后 Console 敲 __threadIdAudit()
  (window as any).__threadIdAudit = () => {
    const log = readLog();
    // eslint-disable-next-line no-console
    console.table(log.map(({ stack, ...rest }) => rest));
    // eslint-disable-next-line no-console
    console.log('[threadId-audit] strip 记录的调用堆栈:');
    log
      .filter((e) => e.type === 'strip')
      .forEach((e, i) => {
        // eslint-disable-next-line no-console
        console.log(`#${i} @ ${e.t}\n${e.stack || '(no stack)'}`);
      });
    return log;
  };

  (window as any).__threadIdAuditClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_SEEN_KEY);
  };
}
